import { Button, Input, Modal } from '@farol-ds/react'
import { sendMetrics } from '@jusbrasil-web/shared-utils-events'
import { ChangeEvent, useEffect, useRef, useState } from 'react'

import { fileToBase64 } from '../../../utils'
import { ACCEPTED_FILE_INPUT } from '../constants'
import { useStudentEnrollmentValidationContext } from '../context'
import { Steps } from '../enums'
import styles from './steps.module.scss'

export function DocumentPreview() {
  const { setSharedData, sharedData, setCurrentStep, eventData, viewer, texts } =
    useStudentEnrollmentValidationContext()
  const inputRef = useRef<HTMLInputElement>(null)
  const [previewSrc, setPreviewSrc] = useState<string | null>(null)
  const pid = viewer?.pid
  const file = sharedData.documentFile

  const title = texts?.documentPreview?.title ?? 'Tudo certo com a foto?'
  const description = texts?.documentPreview?.description ?? (
    <>
      Confira se os dados estão <strong>legíveis</strong> e se o documento ficou{' '}
      <strong>bem iluminado</strong>.
    </>
  )
  const confirmLabel = texts?.documentPreview?.confirmButtonText ?? 'Sim'
  const retryLabel = texts?.documentPreview?.retryButtonText ?? 'Não, quero refazer'

  useEffect(() => {
    sendMetrics('UserAccount.StudentEnrollmentProofPreviewViewed', `${pid}`, {
      ...eventData,
      metadata: { ...eventData?.metadata, mimeType: sharedData.mimeType },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!file) {
      setPreviewSrc(null)
      return
    }

    const objectUrl = URL.createObjectURL(file)
    setPreviewSrc(objectUrl)

    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [file])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) {
      return
    }

    fileToBase64(selectedFile).then((documentData) => {
      setSharedData({
        ...sharedData,
        documentFile: selectedFile,
        documentData,
        documentName: selectedFile.name,
        mimeType: selectedFile.type || undefined,
      })
    })
  }

  const handleSubmit = () => {
    sendMetrics('UserAccount.StudentEnrollmentProofSubmitted', `${pid}`, {
      ...eventData,
      metadata: { ...eventData?.metadata, mimeType: sharedData.mimeType },
    })
    setCurrentStep(Steps.VALIDATING_ENROLLMENT)
  }

  return (
    <>
      <Modal.Header>
        <Modal.HeaderTitle>{title}</Modal.HeaderTitle>
        <Modal.HeaderDescription>{description}</Modal.HeaderDescription>
      </Modal.Header>
      <Modal.Body data-testid="document-preview-step">
        <div className={styles.preview}>
          {previewSrc && (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={previewSrc} alt="Comprovante de matrícula" className={styles.previewImage} />
          )}
        </div>
        <Input
          data-testid="enrollment-document-input-file"
          type="file"
          ref={inputRef}
          accept={ACCEPTED_FILE_INPUT}
          onChange={handleFileChange}
          className={styles.previewInput}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button kind="primary" onClick={handleSubmit}>
          {confirmLabel}
        </Button>
        <Button
          kind="tertiary"
          onClick={() => {
            sendMetrics('UserAccount.StudentEnrollmentProofRetakeClicked', `${pid}`, {
              ...eventData,
            })
            inputRef.current?.click()
          }}
        >
          {retryLabel}
        </Button>
      </Modal.Footer>
    </>
  )
}
